import React, { useMemo } from 'react';
import { Text } from 'tea-component';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { dynamicString } from '@home/utils/dynamicString';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import {
  resolvePrCurrentGameDisplayName,
  formatPrSummaryCardsResponse,
  type IPrGameChannelItem,
  type PrSummaryCardMetricConfig,
} from '../../_utils';
import './index.scss';
import CommonBubble from '@home/components/CommonBubble';

export type PrSummaryCardItem = {
  /** 与 KPI 配置 metricKey 一致，用于稳定 key */
  metricKey?: string;
  title_en?: string;
  title_cn?: string;
  /** 指标说明（EN），标题旁气泡 */
  tooltip_en?: string;
  tooltip_cn?: string;
  value?: string | number;
  sub_en?: string;
  sub_cn?: string;
  /** 主数值 dataFormat，见 @home/utils/dbDataFormat/contant FORMAT_RULE_MAP */
  formatRule?: string;
  /** 副文案为数值时的 dataFormat */
  subFormatRule?: string;
  /** KPI 模板副文案 EN（服务端 formatData 只填 patch，由组件用 dynamicString 渲染） */
  subTemplateEn?: string;
  subTemplateCn?: string;
  subTemplatePatch?: Record<string, unknown>;
};

export interface IPrSummaryCardsProps {
  data?: Record<string, unknown>;
  dataKey?: string;
  /** 当前选中游戏名称（从 prGamesChannels 匹配得出） */
  gameName?: string;
  /** 游戏列表+渠道数据，用于匹配 gameName */
  prGamesChannels?: IPrGameChannelItem[];
  /** 当前选中游戏的 unified_edition_id */
  prHubGameCode?: string;
  /**
   * KPI 指标项（与 `_utils` 中格式一致）。若传入且 Service 侧为透传原始接口数据，
   * 则由本组件调用 `formatPrSummaryCardsResponse` 生成卡片列表。
   */
  summaryMetrics?: PrSummaryCardMetricConfig[];
}

type PrSummaryServiceBlock = {
  data?: { items?: PrSummaryCardItem[]; game_name?: string };
  loading?: boolean;
  errorMsg?: string;
};

/** 接口空值时展示短横线（与需求「短划线」一致，使用 ASCII `-`） */
function dashIfEmpty(raw: unknown): string {
  if (raw === null || raw === undefined || typeof raw === 'object') {
    return '-';
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? String(raw) : '-';
  }
  const s = String(raw).trim();
  return s === '' ? '-' : String(raw);
}

function formatWithRule(raw: unknown, formatRule: string): string {
  if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
    return '-';
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return `${dataFormat({ value: raw, formatRule })}`;
  }
  if (!Number.isNaN(n) && Number.isFinite(n)) {
    return `${dataFormat({ value: n, formatRule })}`;
  }
  return dashIfEmpty(raw);
}

/** 主指标数值：dataFormat，默认千分位整数 */
function formatSummaryMetricValue(raw: unknown, formatRule?: string): string {
  const rule = formatRule && formatRule.trim() !== '' ? formatRule : 'thousandsInit';
  return formatWithRule(raw, rule);
}

/** 副文案：有 subFormatRule 且可解析为数字时格式化，否则原文展示 */
function formatSummarySubValue(raw: unknown, subFormatRule?: string): string {
  if (subFormatRule && subFormatRule.trim() !== '') {
    const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
    if (typeof raw === 'number' && Number.isFinite(raw)) {
      return `${dataFormat({ value: raw, formatRule: subFormatRule })}`;
    }
    if (!Number.isNaN(n) && Number.isFinite(n)) {
      return `${dataFormat({ value: n, formatRule: subFormatRule })}`;
    }
  }
  return dashIfEmpty(raw);
}

function hasSubContent(it: PrSummaryCardItem): boolean {
  const hasTplPatch =
    it.subTemplatePatch &&
    typeof it.subTemplatePatch === 'object' &&
    !Array.isArray(it.subTemplatePatch) &&
    (it.subTemplateEn || it.subTemplateCn);
  if (hasTplPatch) {
    return true;
  }
  return [it.sub_en, it.sub_cn].some(
    (x) => x !== undefined && x !== null && String(x).trim() !== '',
  );
}

/**
 * 副文案：配置了 subTemplate* + patch 时在组件侧走 dynamicString；否则沿用 sub_en/sub_cn + subFormatRule
 */
function resolveSubDisplayText(it: PrSummaryCardItem, isEn: boolean): string {
  const patch = it.subTemplatePatch;
  const hasTpl =
    patch &&
    typeof patch === 'object' &&
    !Array.isArray(patch) &&
    (it.subTemplateEn || it.subTemplateCn);
  if (hasTpl) {
    const primary = isEn ? it.subTemplateEn : it.subTemplateCn;
    const fallback = isEn ? it.subTemplateCn : it.subTemplateEn;
    const tmpl =
      primary && String(primary).trim() !== ''
        ? primary
        : fallback && String(fallback).trim() !== ''
          ? fallback
          : '';
    if (tmpl) {
      return dynamicString(tmpl, patch as Record<string, unknown>);
    }
  }
  const subRaw = isEn ? it.sub_en ?? it.sub_cn : it.sub_cn ?? it.sub_en;
  return formatSummarySubValue(subRaw, it.subFormatRule);
}

const PrSummaryCards: React.FC<IPrSummaryCardsProps> = (props) => {
  const {
    data,
    dataKey = 'prSummaryCards',
    prGamesChannels,
    prHubGameCode,
    summaryMetrics,
  } = props;
  const { locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const block = data?.[dataKey] as PrSummaryServiceBlock | undefined;

  const items = useMemo(() => {
    const rawOuter = block?.data as unknown;
    if (summaryMetrics?.length && rawOuter !== undefined && rawOuter !== null) {
      try {
        return (formatPrSummaryCardsResponse(rawOuter, summaryMetrics).items ?? []) as PrSummaryCardItem[];
      } catch (_) {
        return [];
      }
    }
    return (block?.data?.items ?? []) as PrSummaryCardItem[];
  }, [block?.data, summaryMetrics]);

  const loading = Boolean(block?.loading);
  const isError = Boolean(block?.errorMsg) && !loading;

  /** 从 prGamesChannels 匹配当前游戏名称（URL 可能为 game_code，需与 games 列表 unified_edition_id 对齐） */
  const currentGameName = useMemo(() => {
    const base = (data as Record<string, unknown>) || {};
    return resolvePrCurrentGameDisplayName(
      { ...base, prHubGameCode: prHubGameCode ?? base.prHubGameCode },
      prGamesChannels,
    );
  }, [prGamesChannels, prHubGameCode, data]);

  const cards = useMemo(
    () =>
      items.map((it, idx) => {
        const titleRaw = isEn ? it.title_en ?? it.title_cn : it.title_cn ?? it.title_en;
        const tooltipRaw = isEn ? it.tooltip_en ?? it.tooltip_cn : it.tooltip_cn ?? it.tooltip_en;
        const title = dashIfEmpty(titleRaw);
        const tooltipText =
          tooltipRaw !== undefined && tooltipRaw !== null && String(tooltipRaw).trim() !== ''
            ? String(tooltipRaw)
            : '';
        const value = formatSummaryMetricValue(
          it.value,
          typeof it.formatRule === 'string' ? it.formatRule : undefined,
        );
        const sub = resolveSubDisplayText(it, isEn);
        const showSub = hasSubContent(it);

        const titleNode = (
          <Text parent={tooltipText ? 'span' : 'div'} className="dbh-pr-summary-cards__label" style={{ cursor: tooltipText ? 'pointer' : undefined }}>
            {title}
          </Text>
        );

        return (
          <div key={String(it.metricKey ?? idx)} className="dbh-pr-summary-cards__card">
            <div className="dbh-pr-summary-cards__card-body">
              {tooltipText ? (
                <CommonBubble
                  dark
                  placement="top"
                  arrowPointAtCenter
                  style={{ maxWidth: 420 }}
                  content={<span style={{ whiteSpace: 'pre-line' }}>{tooltipText}</span>}
                >
                  {titleNode}
                </CommonBubble>
              ) : (
                titleNode
              )}
              <div className="dbh-pr-summary-cards__value">{value}</div>
              {showSub ? (
                <div className="dbh-pr-summary-cards__sub">{sub}</div>
              ) : null}
            </div>
          </div>
        );
      }),
    [items, isEn],
  );

  return (
    <Abnormal
      className="dbh-pr-summary-cards"
      isLoading={loading}
      isError={isError}
      isNoData={!loading && !isError && items.length === 0}
      style={{ minHeight: 126, background: '#fff' }}
      loadClassName="dbh-pr-summary-cards__loading"
      component={
        <div className="dbh-pr-summary-cards__wrap">
          <h3 className="dbh-pr-summary-cards__game-name">{currentGameName}</h3>
          <div className="dbh-pr-summary-cards__grid">{cards}</div>
        </div>
      }
    />
  );
};

export default PrSummaryCards;
